<template>
  <div class="credentials-card-container" v-bind:class="{ loading: saving }">
    <div class="credentials-card" v-bind:class="{ loading: saving, error: error}">
      <div class="bank-title" :class="{ 'no-instructions': !source.instructions }">
        <span>
          {{ source.displayName }}
          <div class="nickname">{{ source.name }}</div>
        </span>
      </div>
      <div class="card-bottom" :class="{ error }">
        <ValidationObserver ref="observer" slim>
          <div class="input-wrapper">
            <div v-for="(field, fieldKey) in source.fields" :key="fieldKey">
              <secret-input-field v-if="field.type !== 'password'"
                                :fieldText="field.name"
              />
              <password-input-field v-else
                                    ref="field"
                                    :fieldText="field.name"
                                    :fieldValue="source.fields[fieldKey].value"
                                    :autofocus="true && focusOnLoad"
                                    :updateInput="updateInput(source.credentialsId, fieldKey)"
                                    :validations="field.validation"
              />
            </div>
          </div>
          <card-button :disabled="!saveEnabled || saving" :title="'שמירת פרטים'" :cardStyle="'primary'" :action="save"/>
        </ValidationObserver>
      </div>
      <credentials-validation-error :checkCredentials="source.validations.checkCredentials"
                                    :sourceName="source.sourceName"
                                    :links="source.links"
                                    v-if="error">
      </credentials-validation-error>
      <card-error v-if="source.updateError" :error-message="'תקלה בשמירת הנתונים. נסו שנית.'"/>
      <bank-footer :source="source" />
    </div>
    <progress-loader ref="progress" @stopped="progressStopped"/>
    </div>
</template>

<script>

import _ from 'lodash';
import { mapActions } from 'vuex';
import Segment from '../../../Segment';
import PasswordInputField from '../../card-parts/PasswordInputField.vue';
import CardButton from '../../card-parts/CardButton.vue';
import CredentialsValidationError from './CredentialsValidationError.vue';
import SecretInputField from '../../card-parts/SecretInputField.vue';
import ProgressLoader from '../../ProgressLoader.vue';
import CardError from '../../card-parts/CardError.vue';
import BankFooter from './BankFooter.vue';

export default {
  name: 'CredentialsCard',
  props: [
    'focusOnLoad',
    'source',
  ],
  components: {
    SecretInputField,
    CredentialsValidationError,
    PasswordInputField,
    CardButton,
    CardError,
    ProgressLoader,
    BankFooter,
  },
  data() {
    return {
      saving: false,
      success: true,
    };
  },
  computed: {
    saveEnabled() {
      return _.every(this.source.fields, field => !field.renew || (field.renew && field.value && field.value.length > 3));
    },
    error() {
      return !this.source.validations.checkCredentials.isValid;
    },
  },
  methods: {
    ...mapActions('credentials', [
      'updateCredentialsField', 'updateCredentials', 'updateCredentialsStatus', 'deleteCredentials', 'setSavingInProcess',
    ]),
    async save() {
      const valid = await this.$refs.observer.validate();
      _.forEach(this.$refs.field, field => field.onSave());
      if (valid) {
        Segment.trackUserInteraction('AccountSaveClicked', { name: this.source.sourceName });
        this.saving = true;
        this.setSavingInProcess(true);
        try {
          this.$refs.progress.start();
          const { success } = await this.updateCredentials({ source: this.source, renew: true });
          this.success = success;
        } finally {
          this.$refs.progress.stop();
        }
      }
    },
    async remove() {
      Segment.trackUserInteraction('RemoveAccountClicked', {
        name: this.source.sourceName,
      });
      await this.deleteCredentials({ credentialsId: this.source.credentialsId });
    },
    updateInput(credentialsId, fieldKey) {
      return value => this.updateCredentialsField({ credentialsId, fieldKey, value });
    },
    progressStopped() {
      this.saving = false;
      this.setSavingInProcess(false);
      if (this.success === true) {
        this.updateCredentialsStatus({ source: this.source, status: 'valid' });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../../../../node_modules/base-ui/src/scss/riseup-colors';
  @import '../../../scss/mixins';

  .credentials-card-container {
    position: relative;

    &.loading {
      border-radius: 3px;
      border: 2px solid $riseup_light_blue;
    }

    .credentials-card {
      width: 100%;
      border-radius: 3px;
      box-shadow: 2px 2px 8px 0 $riseup_gray_1;
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      &.loading {
        opacity: 0.05;
      }

      &.error {
        border-radius: 3px;
        border: 2px solid $riseup_coral;
      }

      .bank-title {
        @include card-title;
        margin-bottom: 11px;
        width: 100%;
        display: flex;
        justify-content: space-between;
        padding: 0 30px;
        padding-top: 24px;

        &.no-instructions {
          margin-bottom: 22px;
        }

        .nickname {
          font-size: 20px;
          font-weight: normal;
          font-family: SimplerPro;
        }

        .thanks {
          font-weight: normal;
          font-family: SimplerPro;
          font-size: 20px;
          color: $riseup_green;
          margin: auto 0;
        }

        .remove-container {
          margin: -24px -20px 0;
          padding: 3px 20px 20px 3px;
          cursor: pointer;

          img {
            height: 18px;
          }
        }
      }

      .card-bottom {
        width: 100%;
        padding: 0 30px 30px;

        .input-wrapper {
          margin-bottom: 30px;
        }

        &.error {
          border-bottom: none;
        }
      }


      .seperator {
        height: 1px;
        width: 100%;
        background: $riseup_gray_0;
      }

      .input-container {
        height: 40px;
        margin-bottom: 20px;

        .input-group {
          padding: 0 30px;
          width: 100%;
          height: 40px;
        }
      }
    }
  }
</style>
<style lang="scss">
  // this css isn't scoped because it needs to override the internal css of the loader
  @import '../../../../node_modules/base-ui/src/scss/riseup-colors';
  .credentials-card .save-btn .save-loader {
    position: relative;
    .bar:nth-child(1) {
      background-color: $riseup_light_blue !important;
    }
  }
</style>



// WEBPACK FOOTER //
// src/base-components/credentials/credentials-card/RenewCredentialsCard.vue